import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthFrame, AuthHead, Notice } from "@/components/AuthFrame";
import { Icon } from "@/components/Icon";
import { authErrorText } from "@/features/auth/errors";
import { signedIn, useGo } from "@/features/auth/afterSignIn";
import { isSessionGone } from "@/lib/api";
import { chooseCompany, meQuery, signOut } from "@/lib/auth";
import { validateNextSearch } from "@/lib/next";
import { session } from "@/lib/session";

// Which company, after signing in, for whoever works for more than one (I-partij): one at a time in a
// session, never mixed (docs/auth.md §5). Switching later is in the bar on top (H-g01).

export const Route = createFileRoute("/choose-company")({
  validateSearch: validateNextSearch,
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.fetchQuery(meQuery).catch((err: unknown) => {
      if (isSessionGone(err)) throw redirect({ to: "/login" });
      throw err;
    });
    if (me.mustRelink) throw redirect({ to: "/new-phone" });
    if (me.parties.length <= 1) throw redirect({ to: "/shipments" });
  },
  component: ChooseCompanyScreen,
});

function ChooseCompanyScreen() {
  const { t, i18n } = useTranslation();
  const { next } = Route.useSearch();
  const queryClient = useQueryClient();
  const go = useGo();
  const { data: me } = useSuspenseQuery(meQuery);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const choose = async (partyId: string) => {
    setBusy(partyId);
    setError(null);
    try {
      void go(signedIn(queryClient, await chooseCompany(partyId), next));
    } catch (err) {
      setError(authErrorText(t, err, i18n.language));
      setBusy(null);
    }
  };

  return (
    <AuthFrame label="Portaal Partij kiezen">
      <AuthHead title={t("company.title")} lead={t("company.lead", { count: me.parties.length })} />
      {error && <Notice tone="danger">{error}</Notice>}
      <div className="wl-vstack" style={{ gap: "var(--space-2_5)", alignItems: "stretch" }}>
        {me.parties.map((party) => (
          <button
            key={party.id}
            type="button"
            className="wl-choice-card wl-choice-card--lg"
            disabled={busy !== null}
            aria-busy={busy === party.id || undefined}
            onClick={() => void choose(party.id)}
          >
            <span className="wl-choice-card__icon">
              <Icon name="building" size={20} />
            </span>
            <span className="wl-choice-card__body">
              <span className="wl-choice-card__title">{party.name}</span>
              <span className="wl-choice-card__meta">{[party.code, party.city].filter(Boolean).join(" · ")}</span>
            </span>
            <Icon name="chevronRight" className="wl-choice-card__chev" />
          </button>
        ))}
      </div>
      <div className="wl-auth__actions">
        <span className="wl-auth__foot">{t("company.signedInAs", { name: me.user.name, email: me.user.email })}</span>
        <span className="wl-spacer" />
        <button
          type="button"
          className="wl-btn wl-btn--link"
          onClick={() =>
            void signOut().finally(() => {
              session.ended();
              queryClient.clear();
              window.location.assign("/login");
            })
          }
        >
          {t("user.signOut")}
        </button>
      </div>
    </AuthFrame>
  );
}
